import React, { ReactNode, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { styled } from '@linaria/react';
import { append, dropLast, update } from 'ramda';
import { Button } from '@daedalus/atlas/Button';
import { Icon } from '@daedalus/atlas/Icon';
import { Stepper } from '@daedalus/atlas/Stepper';
import { FormattedMessageWrapper } from '@daedalus/core/src/localization/components/FormattedMessage';
import { DEFAULT_CHILD_AGE } from '@daedalus/core/src/room/business/roomConfiguration/config';
import { AgeField } from './AgeField';
import { guestPickerMessages } from './guestPickerMessages';
type PropsType = {
  /** Number of guests */
  adultGuests: number;
  /** Number of children */
  childGuests: number[];
  /** Room's index. For room index 0 the remove button is not shown. */
  index: number;
  /** Maximum number of guests allowed in the room */
  maxGuests: number;
  /** Maximum number of adult guests allowed in the room */
  maxAdultGuests?: number;
  /** Maximum number of child guests allowed in the room */
  maxChildGuests?: number;
  /** onAdultsChange callback  */
  onAdultsChange: (index: number, value: number) => void;
  /** onChildrenChange callback  */
  onChildrenChange: (index: number, value: number[]) => void;
  /** onRemoveRoom callback  */
  onRemoveRoom?: (index: number) => void;
  /** Whether the inputs should be smaller */
  isCompactLayout?: boolean;
  /** Passing custom title for picker */
  customTitle?: ReactNode;
};
const GuestPickerElement = /*#__PURE__*/styled('div')({
  name: "GuestPickerElement",
  class: "gj5k66n",
  propsAsIs: false
});
const Row = /*#__PURE__*/styled('div')({
  name: "Row",
  class: "rf6lz9x",
  propsAsIs: false
});
const _exp6 = /*#__PURE__*/() => Row;
const HeadingRow = /*#__PURE__*/styled(_exp6())({
  name: "HeadingRow",
  class: "h9l22r0",
  propsAsIs: true
});
const RoomHeading = /*#__PURE__*/styled('h4')({
  name: "RoomHeading",
  class: "rgw1b1u",
  propsAsIs: false
});
const InputLabel = /*#__PURE__*/styled('span')({
  name: "InputLabel",
  class: "irze83u",
  propsAsIs: false
});
const IconWrapper = /*#__PURE__*/styled('span')({
  name: "IconWrapper",
  class: "i7nmrzi",
  propsAsIs: false
});
const AgeSelectorWrapper = /*#__PURE__*/styled('div')({
  name: "AgeSelectorWrapper",
  class: "a1v2d1dy",
  propsAsIs: false
});
const AgeFieldWrapper = /*#__PURE__*/styled('div')({
  name: "AgeFieldWrapper",
  class: "ach2e2z",
  propsAsIs: false
});
const getAdultsMin = (index: number): number => index > 0 ? 0 : 1;
export const GuestPickerLayout = ({
  adultGuests,
  childGuests,
  index,
  maxGuests,
  maxAdultGuests,
  onAdultsChange,
  onChildrenChange,
  onRemoveRoom,
  isCompactLayout = false,
  customTitle
}: PropsType) => {
  const {
    formatMessage
  } = useIntl();
  const handleRemoveRoom = useCallback((index: number) => {
    onRemoveRoom && onRemoveRoom(index);
  }, [onRemoveRoom]);
  const handleNumberOfAdultsChange = useCallback((value: number) => {
    if (value) onAdultsChange(index, value);else handleRemoveRoom(index);
  }, [handleRemoveRoom, index, onAdultsChange]);
  const handleNumberOfChildrenChange = useCallback((value: number) => {
    const newChildGuests = value > childGuests.length ? append(DEFAULT_CHILD_AGE, childGuests) : dropLast(1, childGuests);
    onChildrenChange(index, newChildGuests);
  }, [childGuests, index, onChildrenChange]);
  const handleChildAgeChange = useCallback((childIndex: number, value: number) => {
    const newChildGuests = update(childIndex, value, childGuests);
    onChildrenChange(index, newChildGuests);
  }, [childGuests, index, onChildrenChange]);
  const getChildAgeMessage = useCallback((number: number): string => formatMessage(guestPickerMessages.childAgeWithNumber, {
    number
  }), [formatMessage]);
  const calculateMaxGuests = useCallback((isChildGuest: boolean): number => isChildGuest ? maxGuests - adultGuests : maxGuests - childGuests.length, [adultGuests, childGuests, maxGuests]);
  const calculateMaxAdultGuests = useCallback((): number => Math.min(calculateMaxGuests(false), maxAdultGuests || Infinity), [calculateMaxGuests, maxAdultGuests]);
  const calculateMaxChildGuests = useCallback((): number => calculateMaxGuests(true), [calculateMaxGuests]);
  return <GuestPickerElement>
      <HeadingRow>
        <RoomHeading>
          {customTitle ? customTitle : <FormattedMessageWrapper id="room.reference" defaultMessage="Room {roomNumber} " values={{
          roomNumber: index + 1
        }} />}
        </RoomHeading>
        {index > 0 && <Button size="md" variant="quiet" dataId="removeRoom" onClick={() => {
        handleRemoveRoom(index);
      }}>
            <FormattedMessageWrapper id="removeRoom" defaultMessage="Remove room" />
          </Button>}
      </HeadingRow>
      <Row>
        <InputLabel>
          <IconWrapper>
            <Icon name="User" />
          </IconWrapper>
          <FormattedMessageWrapper id="adults" defaultMessage="Adults" />
        </InputLabel>
        <Stepper name="GuestPickerAdultsNumber" id="GuestPickerAdultsNumber" dataId="GuestPickerAdultsNumber" value={adultGuests} min={getAdultsMin(index)} max={calculateMaxAdultGuests()} onChange={handleNumberOfAdultsChange} size={isCompactLayout ? 'sm' : 'lg'} />
      </Row>
      <Row>
        <InputLabel>
          <IconWrapper>
            <Icon name="Person" />
          </IconWrapper>
          <FormattedMessageWrapper id="children" defaultMessage="Children" />
        </InputLabel>
        <Stepper name="GuestPickerAdultsNumber" id="GuestPickerAdultsNumber" dataId="GuestPickerChildrenNumber" value={childGuests.length > 0 ? childGuests.length : 0} min={0} max={calculateMaxChildGuests()} onChange={handleNumberOfChildrenChange} size={isCompactLayout ? 'sm' : 'lg'} />
      </Row>
      {childGuests.length > 0 && <AgeSelectorWrapper>
          {childGuests.map((childAge, index) => <AgeFieldWrapper key={`${index}+${childAge}`}>
              <AgeField displayName={getChildAgeMessage(index + 1)} value={childAge} onChange={value => handleChildAgeChange(index, value)} size={isCompactLayout ? 'sm' : 'lg'} />
            </AgeFieldWrapper>)}
        </AgeSelectorWrapper>}
    </GuestPickerElement>;
};

require("./GuestPickerLayout.wyw-in-js.css!=!../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./GuestPickerLayout.tsx");